import { useEffect, useState, useRef } from "react";
import { useLocation } from "react-router-dom";
import api from "@/lib/api";
import { Sparkles, X, Loader2, Send, CheckCircle2, ZapIcon } from "lucide-react";

export default function Gr8AgenticPanel({ open, onClose }) {
  const location = useLocation();
  const [prompt, setPrompt] = useState("");
  const [loading, setLoading] = useState(false);
  const [convId, setConvId] = useState(null);
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      answer: "Hi — I'm Gr8Agentic AI, your ERP co-pilot. Ask me anything, or tell me to draft a quotation, PO or job order — I'll prepare it for your one-click approval.",
    },
  ]);
  const [actionStatus, setActionStatus] = useState({}); // {msgIdx: "done" | "error"}
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, loading]);

  const pageContext = location.pathname.split("/")[1] || "dashboard";

  const ask = async (q) => {
    const text = (q ?? prompt).trim();
    if (!text || loading) return;
    setPrompt("");
    setMessages((m) => [...m, { role: "user", text }]);
    setLoading(true);
    try {
      const { data } = await api.post("/copilot/chat", {
        prompt: text,
        conversation_id: convId,
        page_context: pageContext,
      });
      if (data.conversation_id) setConvId(data.conversation_id);
      setMessages((m) => [...m, { role: "assistant", ...data }]);
    } catch (e) {
      setMessages((m) => [...m, { role: "assistant", answer: `Sorry — ${e?.response?.data?.detail || "something went wrong."}` }]);
    } finally {
      setLoading(false);
    }
  };

  const approveAction = async (msgIdx, action) => {
    setActionStatus((s) => ({ ...s, [msgIdx]: "loading" }));
    try {
      const { data } = await api.post(`/copilot/actions/${action.type}`, {
        conversation_id: convId,
        payload: action.payload,
      });
      setActionStatus((s) => ({ ...s, [msgIdx]: "done" }));
      const docName = data?.number || data?.id || "draft";
      setMessages((m) => [...m, { role: "assistant", answer: `✓ Created ${docName}. You'll find it in the matching module.` }]);
    } catch (e) {
      setActionStatus((s) => ({ ...s, [msgIdx]: "error" }));
      setMessages((m) => [...m, { role: "assistant", answer: `Action failed: ${e?.response?.data?.detail || "unknown error"}` }]);
    }
  };

  const SUGGESTIONS_BY_PAGE = {
    sales: ["Draft a quotation for Korle Bu of 50 boxes of SYR-5ML and 20 boxes of GLV-LTX-M.", "Top 5 customers by outstanding balance"],
    procurement: ["Draft a PO to MedSupply Ghana Ltd for items below reorder level.", "List items below reorder level"],
    jobs: ["Open a critical job for Komfo Anokye: ECG-12CH unit not powering on.", "How many open jobs do we have?"],
    items: ["Which items are below reorder level?", "Top 5 items by stock value"],
    customers: ["Top 5 customers by outstanding balance", "Which customers haven't ordered in 30 days?"],
    hr: ["What is total monthly payroll cost?", "How many pending leave requests?"],
    dashboard: ["Top 5 customers by outstanding balance", "Items below reorder level", "Total revenue this month"],
  };
  const suggestions = SUGGESTIONS_BY_PAGE[pageContext] || SUGGESTIONS_BY_PAGE.dashboard;

  return (
    <div
      className={`fixed inset-0 z-50 transition-opacity ${open ? "pointer-events-auto" : "pointer-events-none opacity-0"}`}
      aria-hidden={!open}
      data-testid="gr8agentic-panel"
    >
      <div onClick={onClose} className={`absolute inset-0 bg-black/40 transition-opacity ${open ? "opacity-100" : "opacity-0"}`} />
      <aside className={`absolute top-0 right-0 h-full w-full sm:w-[520px] bg-[#0A0A0A] text-white flex flex-col shadow-2xl transition-transform duration-300 ${open ? "translate-x-0" : "translate-x-full"}`}>
        <header className="px-5 py-4 border-b border-white/10 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Sparkles size={18} className="text-[#FF4F00]" />
            <div>
              <div className="font-heading font-semibold tracking-tight">Gr8Agentic AI</div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-white/40">Co-pilot · Claude Sonnet 4.5 · {pageContext}</div>
            </div>
          </div>
          <button onClick={onClose} data-testid="close-gr8agentic" className="p-2 hover:bg-white/10 rounded-sm">
            <X size={16} />
          </button>
        </header>

        <div ref={scrollRef} className="flex-1 overflow-y-auto p-5 space-y-4">
          {messages.map((m, i) => (
            <div key={i} className={m.role === "user" ? "flex justify-end" : ""}>
              {m.role === "user" ? (
                <div className="max-w-[85%] bg-[#FF4F00] text-white px-4 py-2.5 text-sm rounded-sm" data-testid={`agent-msg-user-${i}`}>{m.text}</div>
              ) : (
                <div data-testid={`agent-msg-assistant-${i}`} className="space-y-2">
                  <div className="text-sm leading-relaxed text-white/90 whitespace-pre-wrap">{m.answer || "—"}</div>

                  {Array.isArray(m.rows) && m.rows.length > 0 && (
                    <div className="border border-white/15 bg-white/[0.04] overflow-x-auto">
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="border-b border-white/10 bg-white/[0.06]">
                            {Object.keys(m.rows[0]).slice(0, 5).map((k) => (
                              <th key={k} className="px-3 py-2 text-left text-[10px] uppercase tracking-[0.15em] text-[#FF4F00]">{k}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {m.rows.map((row, ri) => (
                            <tr key={ri} className="border-b border-white/5">
                              {Object.keys(m.rows[0]).slice(0, 5).map((k) => (
                                <td key={k} className="px-3 py-2 text-white/80 truncate max-w-[160px]">{fmt(row[k])}</td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {m.action && (
                    <div className="border border-[#FF4F00] bg-[#FF4F00]/10 p-3 space-y-2" data-testid={`agent-action-${i}`}>
                      <div className="flex items-center gap-2 text-xs uppercase tracking-[0.15em] text-[#FF4F00]">
                        <ZapIcon size={12} /> Proposed action · {m.action.type.replace(/_/g, " ")}
                      </div>
                      <pre className="text-[11px] font-mono text-white/70 max-h-32 overflow-y-auto bg-black/30 p-2 rounded-sm">{JSON.stringify(m.action.payload, null, 2)}</pre>
                      {actionStatus[i] === "done" ? (
                        <div className="text-xs text-[#0B7A75] inline-flex items-center gap-1"><CheckCircle2 size={12} /> Approved & created</div>
                      ) : (
                        <button
                          onClick={() => approveAction(i, m.action)}
                          disabled={actionStatus[i] === "loading"}
                          data-testid={`approve-action-${i}`}
                          className="w-full bg-[#FF4F00] hover:bg-[#E64600] disabled:opacity-50 text-white text-sm px-3 py-2 inline-flex items-center justify-center gap-1.5"
                        >
                          {actionStatus[i] === "loading" ? <><Loader2 size={14} className="animate-spin" /> Creating…</> : <>{m.action.label || "Approve & create"}</>}
                        </button>
                      )}
                    </div>
                  )}

                  {m.error && <div className="text-xs text-[#FF4F00]">Note: {m.error}</div>}
                </div>
              )}
            </div>
          ))}
          {loading && (
            <div className="flex items-center gap-2 text-xs text-white/60">
              <Loader2 size={14} className="animate-spin text-[#FF4F00]" /> Gr8Agentic AI is thinking…
            </div>
          )}
        </div>

        <div className="p-4 border-t border-white/10 space-y-3">
          <div className="flex flex-wrap gap-2">
            {suggestions.map((s) => (
              <button key={s} onClick={() => ask(s)} data-testid={`agent-sug-${s.slice(0, 12).replace(/\s+/g, "-")}`}
                className="text-[11px] px-2.5 py-1.5 border border-white/15 text-white/70 hover:border-[#FF4F00] hover:text-white rounded-sm transition-colors">
                {s}
              </button>
            ))}
          </div>
          <form onSubmit={(e) => { e.preventDefault(); ask(); }} className="flex gap-2" data-testid="gr8agentic-form">
            <input value={prompt} onChange={(e) => setPrompt(e.target.value)} placeholder="Ask anything or say 'draft …'" data-testid="gr8agentic-input"
              className="flex-1 bg-black/40 border border-white/15 px-3 py-2.5 text-sm focus:border-[#FF4F00] focus:outline-none" />
            <button type="submit" disabled={loading || !prompt.trim()} data-testid="gr8agentic-send"
              className="px-4 bg-[#FF4F00] hover:bg-[#E64600] disabled:opacity-50 text-white rounded-sm"><Send size={14} /></button>
          </form>
        </div>
      </aside>
    </div>
  );
}

function fmt(v) {
  if (v == null) return "—";
  if (typeof v === "number") return Number.isInteger(v) ? v.toLocaleString() : v.toFixed(2);
  if (typeof v === "string" && v.length > 30) return v.slice(0, 28) + "…";
  if (typeof v === "object") return JSON.stringify(v).slice(0, 30);
  return String(v);
}
